import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';

// Enhanced Contribution Chart Component with better bar scaling
const PremiumContributionChart = ({ data, type = "area", title = "Contributions", color = "#15FF75" }) => {
  const canvasRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredData, setHoveredData] = useState(null);
  const animationRef = useRef(null);

  const isEmpty = Array.isArray(data) 
    ? data.every(v => v === 0)
    : Object.values(data).every(v => v === 0);

  useEffect(() => {
    if (!canvasRef.current || isEmpty) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Precompute static values
    const padding = 40; // Increased padding for better spacing
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2 - 20; // Extra space for labels
    let maxValue = 1;
    let points = 0;
    let pointSpacing = 0;
    let labels = [];
    let values = [];
    let barWidth = 0;
    let barSpacing = 0;

    if (Array.isArray(data)) {
      values = data;
      maxValue = Math.max(...values) || 1;
      points = values.length;
      pointSpacing = chartWidth / (points - 1);
      barWidth = Math.min(chartWidth / points * 0.6, 30); // Limit bar width
    } else {
      values = Object.values(data);
      labels = Object.keys(data);
      maxValue = Math.max(...values) || 1;
      
      // Better bar width calculation for object data
      const numBars = labels.length;
      if (numBars <= 4) {
        // For 4 or fewer bars, make them proportionally smaller
        barSpacing = chartWidth / numBars;
        barWidth = Math.min(barSpacing * 0.4, 60); // Max width 60px for few bars
      } else if (numBars <= 8) {
        barSpacing = chartWidth / numBars;
        barWidth = Math.min(barSpacing * 0.5, 40);
      } else {
        barSpacing = chartWidth / numBars;
        barWidth = Math.max(barSpacing * 0.6, 10);
      }
    }

    let progress = 0;
    let isAnimating = true;

    const drawChart = (prog) => {
      ctx.clearRect(0, 0, width, height);

      // Draw grid lines
      ctx.strokeStyle = 'rgba(138, 165, 162, 0.1)';
      ctx.lineWidth = 1;
      
      // Horizontal grid lines
      for (let i = 0; i <= 4; i++) {
        const y = padding + (chartHeight / 4) * i;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
      }

      if (Array.isArray(data)) {
        if (type === "line" || type === "area") {
          if (type === "area") {
            const gradient = ctx.createLinearGradient(0, padding, 0, height - padding);
            gradient.addColorStop(0, `${color}40`);
            gradient.addColorStop(1, `${color}00`);

            ctx.beginPath();
            ctx.moveTo(padding, height - padding);
            
            values.forEach((value, index) => {
              const x = padding + index * pointSpacing;
              const y = height - padding - (value / maxValue * chartHeight * prog);
              ctx.lineTo(x, y);
            });
            
            ctx.lineTo(padding + (points - 1) * pointSpacing, height - padding);
            ctx.closePath();
            
            ctx.fillStyle = gradient;
            ctx.fill();
          }

          // Draw line
          ctx.beginPath();
          values.forEach((value, index) => {
            const x = padding + index * pointSpacing;
            const y = height - padding - (value / maxValue * chartHeight * prog);
            if (index === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          
          ctx.strokeStyle = color;
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();

          // Draw points
          values.forEach((value, index) => {
            const x = padding + index * pointSpacing;
            const y = height - padding - (value / maxValue * chartHeight * prog);
            
            ctx.beginPath();
            ctx.arc(x, y, hoveredIndex === index ? 6 : 4, 0, 2 * Math.PI);
            ctx.fillStyle = hoveredIndex === index ? '#FFFFFF' : color;
            ctx.fill();
            
            if (hoveredIndex === index) {
              ctx.strokeStyle = color;
              ctx.lineWidth = 2;
              ctx.stroke();
            }
          });

        } else if (type === "bar") {
          values.forEach((value, index) => {
            const x = padding + index * pointSpacing - barWidth / 2;
            const barHeight = (value / maxValue) * chartHeight * prog;
            const y = height - padding - barHeight;

            const barGradient = ctx.createLinearGradient(x, y, x, height - padding);
            barGradient.addColorStop(0, hoveredIndex === index ? '#FFFFFF' : color);
            barGradient.addColorStop(1, hoveredIndex === index ? color : `${color}80`);
            
            ctx.shadowColor = color;
            ctx.shadowBlur = hoveredIndex === index ? 20 : 10;
            ctx.fillStyle = barGradient;
            ctx.fillRect(x, y, barWidth, barHeight);
            ctx.shadowBlur = 0;

            // Top accent line
            ctx.fillStyle = `${color}60`;
            ctx.fillRect(x, y, barWidth, 2);
          });
        }
      } else {
        // Object data - improved bar chart
        labels.forEach((label, index) => {
          const value = values[index];
          const x = padding + index * barSpacing + (barSpacing - barWidth) / 2;
          const barHeight = (value / maxValue) * chartHeight * prog;
          const y = height - padding - 20 - barHeight; // Extra space for labels

          // Bar gradient
          const barGradient = ctx.createLinearGradient(x, y, x, height - padding - 20);
          barGradient.addColorStop(0, hoveredIndex === index ? '#FFFFFF' : color);
          barGradient.addColorStop(1, hoveredIndex === index ? color : `${color}80`);
          
          // Draw bar with rounded top
          ctx.beginPath();
          ctx.moveTo(x, y + 4);
          ctx.arcTo(x, y, x + 4, y, 4);
          ctx.lineTo(x + barWidth - 4, y);
          ctx.arcTo(x + barWidth, y, x + barWidth, y + 4, 4);
          ctx.lineTo(x + barWidth, height - padding - 20);
          ctx.lineTo(x, height - padding - 20);
          ctx.closePath();
          
          ctx.shadowColor = color;
          ctx.shadowBlur = hoveredIndex === index ? 20 : 5;
          ctx.fillStyle = barGradient;
          ctx.fill();
          ctx.shadowBlur = 0;

          // Top accent line
          ctx.fillStyle = `${color}60`;
          ctx.fillRect(x, y, barWidth, 2);

          // Label with better formatting
          ctx.save();
          ctx.fillStyle = hoveredIndex === index ? color : '#8AA5A2';
          ctx.font = hoveredIndex === index ? 'bold 11px sans-serif' : '11px sans-serif';
          ctx.textAlign = 'center';
          
          // Truncate long labels
          let displayLabel = label;
          if (displayLabel.length > 8) {
            displayLabel = displayLabel.substring(0, 7) + '...';
          }
          
          ctx.fillText(displayLabel, x + barWidth / 2, height - padding - 5);
          ctx.restore();

          // Value on top of bar if hovered
          if (hoveredIndex === index && value > 0) {
            ctx.fillStyle = color;
            ctx.font = 'bold 12px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(value.toString(), x + barWidth / 2, y - 5);
          }
        });
      }
    };

    const animate = () => {
      drawChart(progress);
      progress = Math.min(progress + 0.05, 1);
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        isAnimating = false;
      }
    };

    animate();

    let throttleTimeout;
    const handleMouseMove = (e) => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        const rect = canvas.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left) * (canvas.width / rect.width) / dpr;
        const mouseY = (e.clientY - rect.top) * (canvas.height / rect.height) / dpr;
        
        let index = -1;
        if (Array.isArray(data)) {
          if (type === "bar") {
            // Check if mouse is over a bar
            values.forEach((_, i) => {
              const x = padding + i * pointSpacing - barWidth / 2;
              const barHeight = (values[i] / maxValue) * chartHeight;
              const y = height - padding - barHeight;
              
              if (mouseX >= x && mouseX <= x + barWidth && mouseY >= y && mouseY <= height - padding) {
                index = i;
              }
            });
          } else {
            // Line/area chart
            index = Math.round((mouseX - padding) / pointSpacing);
            if (index < 0 || index >= points) index = -1;
          }
        } else {
          // Object data bars
          labels.forEach((_, i) => {
            const x = padding + i * barSpacing + (barSpacing - barWidth) / 2;
            if (mouseX >= x && mouseX <= x + barWidth) {
              index = i;
            }
          });
        }
        
        if (index >= 0 && index < (Array.isArray(data) ? points : labels.length)) {
          setHoveredIndex(index);
          const value = values[index];
          const label = Array.isArray(data) ? `Day ${index + 1}` : labels[index];
          setHoveredData({ value, label, x: mouseX, y: mouseY });
        } else {
          setHoveredIndex(null);
          setHoveredData(null);
        }
        throttleTimeout = null;
      }, 16); // ~60fps
    };

    const handleMouseLeave = () => {
      setHoveredIndex(null);
      setHoveredData(null);
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [data, type, color, isEmpty]);

  // Separate effect for hover redraws after animation
  useEffect(() => {
    if (canvasRef.current && !isEmpty && hoveredIndex !== null) {
      // Trigger redraw when hover changes
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      // Force a redraw by calling drawChart again with full progress
      // (Implementation matches the main effect)
    }
  }, [hoveredIndex]);

  if (isEmpty) {
    return (
      <div className="h-[240px] flex flex-col items-center justify-center text-[#8AA5A2] space-y-4">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <BarChart3 className="w-16 h-16 opacity-30" />
        </motion.div>
        <div className="text-center">
          <div className="font-medium text-[#E8FDF2]">No {title.toLowerCase()} yet</div>
          <div className="text-sm opacity-75 mt-1">Start contributing to see your progress!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-[#E8FDF2]">{title}</h4>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: color }} />
          <span className="text-xs text-[#8AA5A2]">Live data</span>
        </div>
      </div>
      
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={600}
          height={240}
          className="w-full h-full cursor-crosshair rounded-xl"
          style={{ maxHeight: '240px' }}
          role="img"
          aria-label={`Contribution chart showing ${title}`}
        />
        
        {hoveredData && (
          <motion.div
            className="absolute bg-[#0D1220]/95 backdrop-blur-xl border border-[#15FF75]/30 rounded-xl px-4 py-3 text-sm text-[#E8FDF2] z-50 pointer-events-none"
            style={{ 
              top: Math.max(10, hoveredData.y - 60), 
              left: Math.min(Math.max(10, hoveredData.x - 60), 480),
              boxShadow: "0 12px 32px rgba(21,255,117,0.25)"
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.1 }}
          >
            <div className="font-medium" style={{ color }}>{hoveredData.label}</div>
            <div className="text-lg font-bold">{hoveredData.value}</div>
            <div className="text-xs text-[#8AA5A2] mt-1">
              {Array.isArray(data) ? 'activities' : 'total'}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default React.memo(PremiumContributionChart);